import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { reportWhiteScreenOfDeath } from '@/OS/lib/wsodReporter';

type RecoverableAppErrorBoundaryProps = {
  appId: string;
  appName: string;
  windowId?: string;
  onRecover: () => void;
  children: React.ReactNode;
};

type RecoverableAppErrorBoundaryState = {
  hasError: boolean;
};

class RecoverableAppErrorBoundary extends React.Component<
  RecoverableAppErrorBoundaryProps,
  RecoverableAppErrorBoundaryState
> {
  state: RecoverableAppErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): RecoverableAppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[RecoverableAppErrorBoundary] ${this.props.appName} crashed`, error);
    void reportWhiteScreenOfDeath({
      scope: 'os-window',
      appId: this.props.appId,
      appName: this.props.appName,
      windowId: this.props.windowId,
      errorMessage: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
      recoveryAction: 'clear_app_state_and_close_window',
    });
    this.props.onRecover();
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full items-center justify-center bg-background p-6">
          <div className="flex max-w-md flex-col items-center gap-4 rounded-xl border bg-card p-6 text-center shadow-sm">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <div className="space-y-2">
              <h2 className="text-lg font-semibold">{this.props.appName} ran into a problem</h2>
              <p className="text-sm text-muted-foreground">
                MapleOS captured this white-screen incident, cleared the saved state for this app, and prevented it from reopening in the same broken state.
              </p>
            </div>
            <Button onClick={this.props.onRecover}>Recover</Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RecoverableAppErrorBoundary;